import React, { useState, useEffect } from 'react';
import {
  Modal,
  Avatar,
  Button,
  Tag,
  Row,
  Col,
  Card,
  Divider,
  Input,
  Form,
  Select,
  Switch,
  message,
  Popover,
  List
} from 'antd';
import {
  EditOutlined,
  CloseOutlined,
  CheckOutlined,
  SaveOutlined,
  CheckCircleTwoTone,
  UserOutlined,
  PhoneOutlined,

  TeamOutlined,
  PlusOutlined,
  CommentOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,

} from '@ant-design/icons';


import './AccountDetailsModal.scss';
import { toast } from 'react-toastify';
import { DEFAULT_PROFILE_IMAGE } from '../../../../constants';
import { isDeepStrictEqual } from 'util';
import { deleteNotes, editNotes, fetchBusinessAccountList, fetchNotesList, saveNotes, updateBusinessAccountDetails } from '../../../../features/members/membersSlice';
import { useDispatch, useSelector } from 'react-redux';
import { appDispatch, RootState } from '../../../../app/store';
import TextArea from 'antd/es/input/TextArea';

const { Option } = Select;
const { confirm } = Modal;
interface UserData {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  display_name: string;
  username: string;
  email: string;
  email_pref: number;
  pager_number: string;
  pager_pref: number;
  profile_pic: string;
  user_type: string;
  source_of_reg_request: string;
  approval_status: string;
  network_roles: number[];
  roles: any;
  network_specialties: number[];
  practice_sites: Array<{
    name: string;
    phone_number_country_code: string;
    phone_number: string;
    position: string;
    country: { name: string; country_name_code: string };
    state: { name: string; country_name_code: string };
    city: { name: string; country_name_code: string };
    address_one: string;
    address_two: string;
    address_three: string;
  }>;
  medical_licenses: Array<{
    license_number: string;
    country: { name: string; country_name_code: string };
    issuing_state: { name: string; state_code: string };
    is_valid: boolean;
    validation_status: string;
    comment?: string;
  }>;
  contact_info: any;

  business_account: any;

  membership_plan: Array<{
    subscription_plan: {
      name: string;
      price: string;
      currency: string;
    };
  }>;
}

interface UserDetailsModalProps {
  fromMemberPage?: boolean;
  isVisible: boolean;
  user: UserData | null;
  selectedUserNetworks: any;
  onClose: () => void;
  onAccept: (user: UserData, isApprove: boolean, Reason?: string) => void;
  onReject: (user: UserData, isApprove: boolean, Reason?: string) => void;
  currentTab?: string;
  onEdit: (user: UserData) => void; // used for update/save
  onDelete: (user: UserData) => void;
  onVerifyLicense?: (user: UserData, licenseIndex: number) => void;
  onRejectLicense?: (user: UserData, licenseIndex: number, reason: string) => void;
  onEditLicense?: (user: UserData, licenseIndex: number, updatedLicense: UserData['medical_licenses'][number]) => void;
  onSaveLicense?: (user: UserData, licenseIndex: number, updatedLicense: UserData['medical_licenses'][number]) => void;
  isLicenseActionLoading?: boolean;
  isEditLicenseLoading?: boolean;
}


export const AccountDetailsModal: React.FC<UserDetailsModalProps> = ({
  fromMemberPage,
  isVisible,
  user,
  selectedUserNetworks,
  onClose,
  onAccept,
  onReject,
  currentTab,
  onEdit,
  onDelete,
  onVerifyLicense,
  onRejectLicense,
  onEditLicense,
  onSaveLicense,
  isLicenseActionLoading = false,
  isEditLicenseLoading = false
}) => {

  const [form] = Form.useForm();
  const dispatch = useDispatch<appDispatch>();

  const [secondaryContact, setSecondaryContact] = useState(
    user?.contact_info?.secondary?.number
  );
  const notesListData = useSelector(
    (state: RootState) => state.members.fetchNotesList.fetchNotesListData
  ) || [];

  const [note, setNote] = useState("");
  const [notes, setNotes] = useState<string[]>([]);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  useEffect(() => {
    dispatch(fetchNotesList({ id: String(user?.id ?? "") }));
  }, [user])

  const [isEditingLeft, setIsEditingLeft] = useState(false);
  const [leftForm] = Form.useForm();

  useEffect(() => {
    if (user) {
      setSecondaryContact(user?.contact_info?.secondary?.number ?? '');

      leftForm.setFieldsValue({
        name: user.name ?? '',
        secondary_phone: user?.contact_info?.secondary?.number ?? '',
      });
    } else {
      leftForm.resetFields();
    }
  }, [user, isVisible]);

  const handleAddNote = () => {

    const thunkArgs = {}
    if (note.trim() !== "") {
      setNotes([note, ...notes]); // add new note at top
      setNote(""); // clear input
      const payload = {
        user_id: user?.id,
        note: note
      }
      dispatch(saveNotes(payload))
        .unwrap()
        .then(async (res) => {
          toast.success("Notes saved successfully");
          setNote("")
          const thunkArgs: any = {}
          await dispatch(fetchNotesList({ id: String(user?.id ?? "") }));

          // handle success UI (toast, navigate, etc.)
        })
        .catch((err) => {
          console.error("Error saving notes", err);
          // handle error UI
        });
    }
  };

  const handleSave = (id: any) => {

    // update locally or trigger API
    console.log("Saving note:", id, editingText);

    dispatch(editNotes({ id, note: editingText }))
      .unwrap()
      .then(async (res) => {
        if (res?.isSuccess) {
          toast.success("Notes updated successfully");
        }

        setNote("")
        const thunkArgs: any = {}
        await dispatch(fetchNotesList({ id: String(user?.id ?? "") }));

        // handle success UI (toast, navigate, etc.)
      })
      .catch((err) => {
        console.error("Error saving notes", err);
        // handle error UI
      });
    // Example local update:
    const updated = notesListData.map((note: any) =>
      note?.id === id ? { ...note, note: editingText } : note
    );

    setNotes(updated);

    setEditingId(null);
  };


  const onDeleteNote = (id: any, noteText?: string) => {
    confirm({
      title: "Delete Note Confirmation",
      icon: <ExclamationCircleOutlined />,
      centered: true,
      content: (
        <p>
          Are you sure you want to delete this note?
          <br />
          <i style={{ color: "#777b80" }}>{noteText}</i>
        </p>
      ),
      onOk() {
        const payload = {
          id: id, // send note id to backend
        };

        dispatch(deleteNotes(payload)).unwrap()
          .then(async () => {
            toast.success("Notes deleted  successfully");
            await dispatch(fetchNotesList({ id: String(user?.id ?? "") }));
          })
          .catch(() => {
            // error handling if needed
          });// <-- your redux action here
      },
      onCancel() { },
    });
  };

  const handleSaveLeftColumn = async () => {
    const values = await leftForm.validateFields();

    // 🔥 ONLY CHANGE ADDED
    if (!values.name || values.name.trim() === "") {
      toast.error("Please enter display name");
      return;
    }

    const secondaryPhone = values.secondary_phone?.trim();
    const primaryPhone = values.primary_phone?.trim() ?? null;

    if (!user) return;

    let updatedUser: UserData = { ...user };

    if (values.name && values.name !== user.name) {
      updatedUser.name = values.name;
    }

    if (secondaryPhone && secondaryPhone !== user.contact_info?.[1]?.number) {
      updatedUser.contact_info = [
        {
          ...user.contact_info?.[0],
          country_code: '',
          number: primaryPhone ? primaryPhone : '',
          number_status: "PRIMARY",
          pref: 1,
        },
        {
          ...user.contact_info?.[1],
          country_code: '',
          number: secondaryPhone ? secondaryPhone : '',
          number_status: "SECONDARY",
          pref: 2,
        },
      ];

    } else if (!secondaryPhone) {
      delete updatedUser?.contact_info;
    }

    console.log("Final Updated User Payload:", updatedUser);

    dispatch(updateBusinessAccountDetails(updatedUser))
      .then((data) => {
        if (data.payload.isSuccess) {
          toast.success(data.payload.message);

          const newSecondary = updatedUser?.contact_info?.[1]?.number || "";
          setSecondaryContact(newSecondary);

          leftForm.setFieldsValue({
            secondary_phone: newSecondary,
          });

          dispatch(fetchBusinessAccountList({ searchquery: "", pageNumber: 1 }));
          setIsEditingLeft(false);
        }
      });
  };


  return (
    <Modal
      open={isVisible}
      onCancel={() => {
        setIsEditingLeft(false);
        onClose();
      }}
      footer={null}
      width={'60%'}
      className="user-details-modal fixed-height-modal"
      centered={true}
      bodyStyle={{
        padding: '24px',
        background: 'var(--modal-background-color)',
        height: '650px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {user && (
        <div className="user-details-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="modal-scroll-content" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '.75rem', minHeight: 0 }}>
            <Form form={leftForm} layout="vertical">

              <Row gutter={[24, 24]} className="profile-section">
                <Col sm={24} md={24} lg={24} xl={24}>
                  <Card className="profile-card" bodyStyle={{ padding: '24px' }}>
                    <div className="profile-header">
                      <Avatar
                        size={100}
                        src={leftForm.getFieldValue('profile_pic') || user.profile_pic || DEFAULT_PROFILE_IMAGE}
                        alt={user.display_name}
                        className="profile-avatar"
                      />

                      <div className="profile-info" style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                          <>
                            <h2 className="profile-name">
                              {!isEditingLeft ? (
                                <div className="info-item">
                                  <span className="profile-name">{leftForm.getFieldValue("name") || user?.name}</span>
                                </div>
                              ) : (
                                <Form.Item name="name" style={{ margin: 0 }}>
                                  <Input placeholder="Enter display name" onChange={(e) => leftForm.setFieldsValue({ name: e.target.value })} />
                                </Form.Item>
                              )}
                            </h2>
                            <div className="profile-actions" style={{ display: 'flex', gap: '8px' }}>
                              <Button
                                onClick={() => {
                                  setIsEditingLeft(true);
                                  leftForm.setFieldsValue({
                                    secondary_phone: secondaryContact
                                  });
                                }} icon={!isEditingLeft ? <EditOutlined /> : null}
                                shape="circle"
                                size="middle"
                                className="edit-btn"
                                style={{ background: 'transparent' }}
                              />
                            </div>
                          </>
                        </div>

                        <Row gutter={[24, 12]}>
                          <Col sm={24} md={24} lg={8} xl={8}>
                            <div className="info-label">Username</div>
                            <div className="info-value">{user?.username}</div>
                          </Col>

                          <Col sm={24} md={24} lg={8} xl={8}>
                            <div className="info-label">ROLE</div>
                            <div className="info-value">{user?.roles?.[0]}</div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>

              {/* 2️⃣ BUSINESS + ROLE + CONTACT ROW */}
              <Row gutter={[24, 24]} className="info-grid">

                {/* BUSINESS INFO COLUMN */}
                <Col sm={24} md={24} lg={12} xl={12}>
                  <Card
                    title={
                      <div className="card-title">
                        <PhoneOutlined className="card-icon contact-icon" />
                        Business Account Information
                      </div>
                    }
                    size="small"
                    className="info-card"
                    headStyle={{ background: 'var(--country-dropdown-hover)', color: 'white' }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Row gutter={[16, 12]}>
                      <Col span={24}>
                        <div className="info-item">
                          <div className="info-label">Business Name</div>
                          <div className="info-value">{user?.business_account?.business_name ?? "N/A"}</div>
                        </div>
                      </Col>

                      <Col span={24}>
                        <div className="info-item">
                          <div className="info-label">Tax ID</div>
                          <div className="info-value">{user?.business_account?.tax_id ?? "N/A"}</div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>

                {/* CONTACT INFO COLUMN */}
                <Col sm={24} md={24} lg={12} xl={12}>
                  <Card
                    title={
                      <div className="card-title">
                        <PhoneOutlined className="card-icon contact-icon" />
                        Contact Information
                      </div>
                    }
                    size="small"
                    className="info-card"
                    headStyle={{ background: 'var(--country-dropdown-hover)', color: 'white' }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Row gutter={[16, 12]}>
                      <Col span={24}>
                        <div className="info-item">
                          <div className="info-label">Email</div>
                          <div className="info-value">{user?.email}</div>
                        </div>
                      </Col>

                      <Col span={24}>
                        <div className="info-item">
                          <div className="info-label">Primary Phone</div>
                          <div className="info-value">
                            {user.contact_info?.primary?.number === null || '' ? "N/A" : user.contact_info?.primary?.number}
                          </div>
                        </div>
                      </Col>

                      <Col span={24}>
                        <div className="info-item">
                          <div className="info-label">Secondary Phone</div>

                          {isEditingLeft ? (
                            <Form.Item name="secondary_phone"

                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (!value) return Promise.resolve(); // optional field

                                    const internationalPhoneRegex = /^\+[1-9]\d{1,14}$/;

                                    if (internationalPhoneRegex.test(value)) {
                                      return Promise.resolve();
                                    }

                                    return Promise.reject(
                                      "Enter a valid international phone number in +CountryCode format (e.g., +14155552671)"
                                    );
                                  }
                                }
                              ]}

                              style={{ margin: 0 }}>
                              <Input
                                placeholder="Enter secondary number"
                              />
                            </Form.Item>
                          ) : (
                            <div className="info-value">
                              {leftForm.getFieldValue("secondary_phone") || secondaryContact || "N/A"}
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>


                <Col sm={24} md={24} lg={12} xl={12}>
                  <Card
                    title={
                      <div className="card-title">
                        <PhoneOutlined className="card-icon contact-icon" />
                        Network Information
                      </div>
                    }
                    size="small"
                    className="info-card"
                    headStyle={{ background: 'var(--country-dropdown-hover)', color: 'white' }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Row gutter={[16, 12]}>
                      <Col span={24}>

                        <div className="info-labelnetwork">Networks created by {user?.username}</div>

                        {(
                          // user?.networks?.length > 0
                          // ? user.networks           // Dynamic data
                          // :
                          ["dn1261", "dn1281", "dn3242", "dn3331","dn10","dn001122"] // Hardcoded fallback
                        ).map((net, index) => (
                          <Row key={index} style={{ marginBottom: 8 }}>
                            <Tag color="blue" className="network-chip">{net}</Tag>
                          </Row>
                        ))}

                      </Col>
                    </Row>
                  </Card>
                </Col>
                <Col sm={24} md={24} lg={12} xl={12}>
                  <Card
                    title={
                      <div
                        className="card-title"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>
                          <CommentOutlined
                            className="card-icon contact-icon"
                            style={{ marginRight: 8 }}
                          />
                          Notes
                        </span>
                        <Button
                          size="small"
                          type="primary"
                          shape="circle"
                          icon={<PlusOutlined size={10} />}
                          onClick={handleAddNote}
                        />
                      </div>
                    }
                    size="small"
                    className="info-card"
                    headStyle={{
                      background: "var(--country-dropdown-hover)",
                      color: "white",
                    }}
                    bodyStyle={{ padding: "20px" }}
                  >
                    <Row gutter={[16, 12]}>
                      <TextArea
                        placeholder="Please add your notes here"
                        // value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                      />
                    </Row>

                   <div
  style={{
    marginTop: 16,
    maxHeight: 300, // adjust as needed
    overflowY: "auto",
    paddingRight: 4,
  }}
>
  <List
    dataSource={notesListData}
    renderItem={(item: any) => (
      <Card
        key={item.id}
        size="small"
        style={{
          marginBottom: 8,
          background: "#3e4348",
          color: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          {/* LEFT SIDE (TEXT OR TEXTAREA) */}
          <div style={{ width: "80%" }}>
            {editingId === item.id ? (
              <>
                <textarea
                  style={{
                    width: "100%",
                    minHeight: 60,
                    background: "#21262c",
                    color: "white",
                    borderRadius: 6,
                    padding: 6,
                    border: "1px solid #575c61",
                  }}
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                />
                <div style={{ marginTop: 4 }}>
                  <small style={{ color: "#c7cace" }}>Editing…</small>
                </div>
              </>
            ) : (
              <>
                <div>
                  {item.note && item.note !== "{}" ? (
                    item.note
                  ) : (
                    <i style={{ color: "#95989d" }}>Empty note</i>
                  )}
                </div>
                <small style={{ color: "#c7cace" }}>
                  {new Date(item.created_at).toLocaleString()}
                </small>
              </>
            )}
          </div>

          {/* RIGHT SIDE - ICONS */}
          <div style={{ display: "flex", gap: 12 }}>
            {editingId === item.id ? (
              <>
                {/* Save */}
                <CheckOutlined
                  style={{ fontSize: 18, color: "lightgreen", cursor: "pointer" }}
                  onClick={() => handleSave(item.id)}
                />
                {/* Cancel */}
                <CloseOutlined
                  style={{ fontSize: 18, color: "orange", cursor: "pointer" }}
                  onClick={() => setEditingId(null)}
                />
              </>
            ) : (
              <>
                {/* Edit */}
                <EditOutlined
                  style={{ fontSize: 16, color: "#1a865f", cursor: "pointer" }}
                  onClick={() => {
                    setEditingId(item.id);
                    setEditingText(item.note);
                  }}
                />
                {/* Delete */}
                <DeleteOutlined
                  style={{ fontSize: 16, color: "#d93a45", cursor: "pointer" }}
                  onClick={() => onDeleteNote(item.id, item.note)}
                />
              </>
            )}
          </div>
        </div>
      </Card>
    )}
  />
</div>
                  </Card>
                </Col>

              </Row>
            </Form>
          </div>

          {/* Footer Actions */}
          {isEditingLeft && (
            <div className="modal-footer">
              <Button danger size="large" onClick={() => setIsEditingLeft(false)} className="reject-btn">
                Cancel
              </Button>
              <Button type="primary" size="large" onClick={handleSaveLeftColumn} className="accept-btn">
                Update
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

